// MACHINERY V2 (Stroje) — Fleet V3 body: the dense table view (M2.13).
//
// Spec: MACHINERY_V2_IMPLEMENTATION_TICKET.md §8 M2.13 + desktop/README.md §V3.
// Rendered truth: desktop/screenshots/v3-table--refined-claude.png and
// v3-table--machineid-native.png (both viewed before any code was written).
//
// THIS IS A BODY, NOT A PAGE — the same contract FleetV1Page and FleetV2Page
// keep. FleetPage owns the shell (rail, toolbar, dialogs, readiness popup,
// toast) AND the single data hook, and swaps this component in when
// `fleetView === "V3"`. `useFleetPageData` / `useFleetTelemetry` are never
// called here, so adding a third view added no second poller.
//
// SECTIONS ARRIVE PRE-GROUPED, FROM THE SAME MAP THE OTHER TWO VIEWS RENDER
// (M2.3 ruling (i) — one grouping key, not two). This view sections nothing
// itself and reuses FleetGroupHeader, so the table's per-type group headers ARE
// the grouped view's headers — same counts, same chip vocabulary, same collapse
// keys. A group collapsed in V2 is collapsed here.
//
// SORTING IS PER SECTION, because each section is its own <table>. The ordering
// itself lives in FleetPage (see its `tableSort` note) so a V3 -> V1 -> V3 round
// trip keeps it, and resets with the grouping dimension.
//
// ============================================================
//  M2.16b — THREE WIDTHS, ONE SORT
// ============================================================
//   < 1024       a dense LIST + the ZORADIŤ chip row (FleetTablePhoneView).
//   1024 - 1279  the REAL `.ms-table`, minus PRIPOJ. and 30 DNÍ (hidden by CSS,
//                machinery-v2-table.scss), with the reduced min-width floor
//                FLEET_TABLE_MIN_WIDTH_COMPACT and 44px row kebabs.
//   >= 1280      untouched. Full column set, `min-width: 1000`, same DOM.
//
// >>> THE LIST/TABLE BOUNDARY IS 1024, NOT 768 (review finding F5). <<< It was
// 768 — the only tier boundary the shared hook offered — which put the real
// seven-column table on a 768px tablet where the fixed columns alone still total
// 666px against a ~740px content box. The package's rule is "no horizontal table
// scroll on phone, EVER", and the reason behind it does not stop at 767: a dense
// index you have to drag sideways to read is not an index at 800px either. So
// the list serves 768-1023 too, and the footnote under it names the width where
// the columns actually appear instead of promising a "full" table that is not
// full until 1280.
//
// >>> BOTH WIDTH DECISIONS COME FROM MEDIA QUERIES, NOT FROM A WIDTH NUMBER. <<<
// `useV2MediaQuery` (v2/useV2MediaQuery.ts) evaluates the SAME query strings the
// two v2 stylesheets are fenced in, and evaluates them ON MOUNT. That is what
// keeps this view's JS branch and the CSS column-hiding on the same side of the
// boundary at a fractional viewport — the F4 defect, whose cited symptom was
// exactly "the table's column hiding silently disables while the reduced
// minWidth: 700 still applies". This view was the FIRST consumer of that hook;
// at review finding F-A the whole v2 module was converted to it, so there is no
// longer a second, integer-width reader anywhere under `machinery/v2/`.
//
// >>> THE SORT IS COMPUTED ONCE, ABOVE THE BRANCH. <<< Both surfaces consume the
// same memo, so the list and the tablet table are ordered by literally the same
// `sortMachines` call over the same state — and the chips at the top of the list
// write to the page's sort, which is what the table headers read. There is no
// list ordering and no table ordering, only THE ordering.
//
// The memo also has to sit above the branch because it is a HOOK: a tablet that
// rotates past 1024 must not change how many hooks this component ran.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React, { useMemo } from "react";

import useTranslation from "../../../../../shared/hooks/useTranslation";
import {
  V2_BELOW_DESKTOP,
  V2_DENSE_LIST,
  useV2MediaQuery,
} from "../../useV2MediaQuery";
import FleetGroupHeader from "../FleetGroupHeader";
// Type-only: the three bodies share ONE prop contract (M2.3 ruling (i)); a value
// import would pull the grouped view into this chunk for nothing.
import type { FleetV2PageProps } from "../FleetV2Page";
import { countStates } from "../fleetSelectors";
import { sectionTitle } from "../fleetGroupBy";
import FleetTable from "./FleetTable";
import FleetTablePhoneView from "./FleetTablePhoneView";
import {
  FLEET_TABLE_MIN_WIDTH_COMPACT,
  FleetTableSort,
  FleetTableSortColumn,
  sortMachines,
} from "./fleetTableSort";

export interface FleetTableViewProps extends FleetV2PageProps {
  sort: FleetTableSort;
  onSortChange: (column: FleetTableSortColumn) => void;
}

const FleetTableView: React.FC<FleetTableViewProps> = ({
  sections, states, telemetry, readiness, newMachineId, collapsedGroups,
  onToggleGroup, buildActions, onOpenProfile, onOpenReadiness, sort, onSortChange,
}) => {
  const { translate, getCurrentLanguage } = useTranslation("myMachinery");
  const lang = getCurrentLanguage();
  // TWO queries, ONE listener each, read ONCE for the whole view and passed
  // down — never per section and never per row (review finding F10).
  const denseList = useV2MediaQuery(V2_DENSE_LIST);
  const belowDesktop = useV2MediaQuery(V2_BELOW_DESKTOP);

  // Sorted once per (sections, sort, states, telemetry) rather than on every
  // render: the shell re-renders on each 60 s telemetry tick, and re-sorting
  // every section on an unrelated re-render would be pure waste.
  const sorted = useMemo(
    () =>
      Object.entries(sections).map(
        ([key, list]) =>
          [key, list, sortMachines(list, sort, states, telemetry, lang)] as const
      ),
    [sections, sort, states, telemetry, lang]
  );

  // Everything under 1024 gets the list. Below the memos, so the hook count is
  // stable across a rotation; above the table markup, so the two never both
  // mount.
  if (denseList) {
    return (
      <FleetTablePhoneView
        sorted={sorted}
        states={states}
        telemetry={telemetry}
        newMachineId={newMachineId}
        collapsedGroups={collapsedGroups}
        onToggleGroup={onToggleGroup}
        onOpenProfile={onOpenProfile}
        sort={sort}
        onSortChange={onSortChange}
      />
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18, minWidth: 0 }}>
      {sorted.map(([key, list, rows]) => (
        <div key={key} style={{ minWidth: 0 }}>
          {/* Collapse state stays keyed on the RAW group key; only the label is
              translated (see sectionTitle). */}
          {/* marginBottom={0}: the header bar sits FLUSH on the table card, as
              the artboard draws it and as the prop's own doc describes. Without
              it the header falls back to V2's default 10 and floats above
              nothing. Sections stay separated by the parent's gap: 18. */}
          <FleetGroupHeader
            title={sectionTitle(key, translate)}
            machineCount={list.length}
            counts={countStates(list, states)}
            collapsed={collapsedGroups.has(key)}
            onToggle={() => onToggleGroup(key)}
            marginBottom={0}
            // The 44px collapse target below 1280, decided ONCE here rather than
            // by a breakpoint call inside every header (F10). `undefined` at
            // desktop emits no declaration at all.
            minHeight={belowDesktop ? 44 : undefined}
          />
          {!collapsedGroups.has(key) && (
            <FleetTable
              // Undefined at desktop => FleetTable's own default, i.e. the
              // unchanged 1000. See FLEET_TABLE_MIN_WIDTH_COMPACT.
              minWidth={belowDesktop ? FLEET_TABLE_MIN_WIDTH_COMPACT : undefined}
              touchMenu={belowDesktop}
              machines={rows}
              telemetry={telemetry}
              readiness={readiness}
              newMachineId={newMachineId}
              sort={sort}
              onSortChange={onSortChange}
              buildActions={buildActions}
              onOpenProfile={onOpenProfile}
              onOpenReadiness={onOpenReadiness}
            />
          )}
        </div>
      ))}
    </div>
  );
};

export default FleetTableView;
